import * as React from 'react';
import { FORM_ERROR, FormApi } from 'final-form';
import { useTranslation } from 'react-i18next';

import Login from './Login/Login';
import { useResponseErrorParser } from './hooks';
import ForgotPassword from './ForgotPassword/ForgotPassword';
import { useRenderers } from '../../../../renderProps';

import {
	LoginType,
	NewFfpAccountParameters,
	useCreateFfpAccountMutation,
	useFfpDefaultAccountQuery,
	useGetAccountInfoQuery
} from '@websky/graphql';
import { useTheme } from '../../../../theme';
import { ApolloError } from 'apollo-boost';
import ModalConfirm from '../../../../ModalConfirm';

import Link from '../../../../Link';
import { Hello } from '../../../../Icons';
import { Сongratulations } from '../../../Icons';
import { UserInfo } from '../../../../Account/types';
import CreateAccountWithCheckbox from './CreateAccount/CreateAccountWithCheckbox/CreateAccountWithCheckbox';

export enum ConnectType {
	Login = 'Login',
	Reset = 'Reset',
	Create = 'Create'
}

export interface Props {
	userInfo: UserInfo;
	authType?: 'update' | 'authenticate';
	type?: ConnectType;
	showRegisterAction?: boolean;
	isOpen?: boolean;
	onLoyaltyCardSaved?: () => Promise<void> | void;
	onClose?: () => void;
	onGoToSettings: () => void;
	onDataLoading?: (state: boolean) => void;
}

const LoyaltyConnect: React.FunctionComponent<Props> = ({
	type: connectType = ConnectType.Login,
	isOpen,
	onClose,
	onDataLoading,
	...props
}) => {
	const { t } = useTranslation('Loyalty');
	const { LoyaltyConnect: css } = useTheme('Loyalty');

	const [type, typeChange] = React.useState<ConnectType>(connectType);

	const newAccFormRef = React.useRef<FormApi<Record<string, string>>>(null);

	React.useEffect(() => {
		typeChange(isOpen ? connectType : null);
	}, [connectType, isOpen]);

	const { parseResponse } = useResponseErrorParser();
	const [error, setError] = React.useState<string>(null);

	const [createAccountSuccess, setCreateAccountSuccess] = React.useState(false);
	const [createAccountLoading, setCreateAccountLoading] = React.useState(false);
	const [showSuccessCreateModal, setShowSuccessCreateModal] = React.useState(false);

	const { data, loading: isDefaultDataLoading } = useFfpDefaultAccountQuery();
	const { data: accountInfo, loading: accountInfoLoading } = useGetAccountInfoQuery({
		fetchPolicy: 'no-cache',
		skip: type !== ConnectType.Create
	});

	React.useEffect(() => {
		onDataLoading?.(accountInfoLoading);
	}, [accountInfoLoading]);

	const [createAccountMutation] = useCreateFfpAccountMutation();

	const onError = (errors: readonly Partial<Error>[]) => {
		const parseMessage = parseResponse(errors);
		setError(parseMessage);

		return { [FORM_ERROR]: parseMessage };
	};

	const createAccountRequest = async (newFfpAccountData: NewFfpAccountParameters[]): Promise<object> => {
		setCreateAccountLoading(true);

		try {
			const { data } = await createAccountMutation({
				variables: {
					params: newFfpAccountData
				}
			});

			if (!data?.CreateFfpAccount?.result) {
				// TODO: HACK fix it
				if (data.CreateFfpAccount.message === 'Сообщение программы: invalid FFP LK system') {
					setCreateAccountSuccess(data.CreateFfpAccount.result);
					setTimeout(() => typeChange(ConnectType.Login));

					return undefined;
				}

				return onError([data.CreateFfpAccount]);
			} else {
				setShowSuccessCreateModal(true);

				setTimeout(() => typeChange(ConnectType.Login));

				setTimeout(() => setShowSuccessCreateModal(false), 5000);

				return undefined;
			}
		} catch (err) {
			setCreateAccountSuccess(false);

			if (err instanceof Error && err.message) {
				return onError([err]);
			}

			if (err instanceof ApolloError && err.graphQLErrors?.[0]?.message) {
				return onError(err.graphQLErrors);
			}

			return onError([{ message: 'Unknown error' }]);
		} finally {
			setCreateAccountLoading(false);
		}
	};

	const toCreateAccount = () => {
		if (props.showRegisterAction) {
			typeChange(ConnectType.Create);
		}
	};

	const toResetPassword = () => {
		typeChange(ConnectType.Reset);
	};

	const closeErrorHandler = () => setError(null);

	const hasConfirmedEmail = accountInfo?.CurrentUser?.authMethods?.some(
		method => method.loginType === LoginType.Email && method.confirmed
	);

	const [isCreateAccountButtonDisabled, setIsCreateAccountButtonDisabled] = React.useState(false);

	const { CreateFfpAccountForm: CreateFfpAccountFormOverride = CreateAccountWithCheckbox } = useRenderers();

	return (
		<>
			<ModalConfirm
				classes={{ root: css.warning__modal }}
				title={t('Oops...')}
				size="medium"
				isOpen={
					isOpen &&
					!accountInfoLoading &&
					!isDefaultDataLoading &&
					!hasConfirmedEmail &&
					type === ConnectType.Create
				}
				onClose={onClose}
				onCancel={onClose}
				confirmBtn={t('Fill email')}
				onConfirm={props.onGoToSettings}
			>
				{t(
					'To continue registering in the loyalty system, fill out and confirm your email in your personal account.'
				)}
			</ModalConfirm>
			<ModalConfirm
				size="medium"
				onClose={onClose}
				isOpen={isOpen && type === ConnectType.Create && hasConfirmedEmail}
				isLoading={createAccountLoading}
				title={
					<div className={css.createTitle}>
						<div>{Hello}</div>
						<div>{t('Create account')}</div>
					</div>
				}
				confirmBtn={t('Create')}
				disableConfirmBtn={isCreateAccountButtonDisabled}
				buttonsExtra={
					<>
						<Link action={() => typeChange(ConnectType.Login)}>{t('I have a card')}</Link>
					</>
				}
				onConfirm={() => {
					newAccFormRef.current?.submit();
				}}
				scroll="body"
			>
				<CreateFfpAccountFormOverride
					initialValues={data}
					onCreateAccount={createAccountRequest}
					success={createAccountSuccess}
					toLogin={() => typeChange(ConnectType.Login)}
					error={error}
					closeError={closeErrorHandler}
					formRef={newAccFormRef}
					isOpen={isOpen && type === ConnectType.Create && hasConfirmedEmail}
					onErrorsChanges={hasErrors => {
						setIsCreateAccountButtonDisabled(hasErrors);
					}}
				/>
			</ModalConfirm>
			<ModalConfirm
				className={css.login__modal_content}
				size="medium"
				onClose={onClose}
				isOpen={isOpen && type === ConnectType.Login && !showSuccessCreateModal}
				hideFooter={true}
				hideTitle={true}
			>
				<Login
					onLoyaltyCardSaved={props.onLoyaltyCardSaved}
					toCreateAccount={toCreateAccount}
					toResetPassword={toResetPassword}
					showRegisterAction={props.showRegisterAction}
					queryType={props.authType}
				/>
			</ModalConfirm>
			<ModalConfirm
				size="medium"
				onClose={onClose}
				isOpen={isOpen && type === ConnectType.Reset}
				hideFooter={true}
				hideTitle={true}
			>
				<ForgotPassword toLogin={() => typeChange(ConnectType.Login)} email={props.userInfo?.email} />
			</ModalConfirm>

			<ModalConfirm
				classes={{ content: css.createSuccess__modal_content }}
				size="medium"
				onClose={() => setShowSuccessCreateModal(false)}
				onConfirm={() => setShowSuccessCreateModal(false)}
				confirmBtn={t('Login')}
				isOpen={isOpen && type === ConnectType.Login && showSuccessCreateModal}
				title={
					<div className={css.createSuccess__modal_titleWrapper}>
						<span>{Сongratulations}</span>
						<span className={css.createSuccess__modal_title}>
							{t('Congratulations on your successful registration!')}
						</span>
					</div>
				}
			>
				<div>{t('The card number and password have been sent by e-mail')}</div>
			</ModalConfirm>
		</>
	);
};

export default LoyaltyConnect;
